import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, Truck, Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { fmtPrice, SHIP_RATES, prodName } from "../data/products";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const CartDrawer = () => {
  const { L, lang, cart, products, setQty, removeItem, count, subtotal, freeShipQty, cartOpen, setCartOpen } = useStore();
  const [dest, setDest] = useState("qc");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", apt: "", city: "", province: "", postal: "" });
  const [sending, setSending] = useState(false);

  const items = cart
    .map((i) => ({ ...i, product: products.find((p) => p.slug === i.slug) }))
    .filter((i) => i.product);

  const freeShipping = count >= freeShipQty;
  const shipping = freeShipping ? 0 : SHIP_RATES[dest];
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        destination: dest,
        origin_url: window.location.origin,
        lang,
        customer_name: form.name,
        customer_email: form.email,
        phone: form.phone,
        shipping_address: form.address,
        shipping_apt: form.apt,
        shipping_city: form.city,
        shipping_province: form.province,
        shipping_postal: form.postal,
      });
      if (res.data && res.data.checkout_url) {
        window.location.assign(res.data.checkout_url);
        return;
      }
      toast.error(L.newsletter.err);
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue. Réessayez." : "Something went wrong. Try again.");
    }
    setSending(false);
  };

  const close = () => setCartOpen(false);

  const destOptions = [
    ["qc", L.cart.destQc],
    ["roc", L.cart.destRoc],
    ["us", L.cart.destUs],
    ["eu", L.cart.destEu],
  ];

  return (
    <AnimatePresence>
      {cartOpen && (
        <motion.div className="fixed inset-0 z-[90]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="cart-drawer">
          <div className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" onClick={close} />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full max-w-md bg-onyx border-l border-gold/25 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-gold/15 shrink-0">
              <h3 className="font-display text-xl text-ivory" data-testid="cart-title">{L.cart.title}</h3>
              <button data-testid="cart-close-button" onClick={close} aria-label="Close" className="p-2 text-ivory/70 hover:text-gold transition-colors">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 text-center" data-testid="cart-empty">
                <p className="text-sand text-sm">{L.cart.empty}</p>
                <Link to="/shop" onClick={close} className="btn-gold rounded-full px-7 py-3 text-[0.7rem] tracking-[0.25em] uppercase font-semibold" data-testid="cart-empty-cta">
                  {L.cart.emptyCta}
                </Link>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="px-6 pt-5">
                  <div className="flex items-center gap-2.5 p-3 rounded-xl border border-gold/25 bg-gold/5" data-testid="cart-free-shipping-note">
                    <Truck className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} />
                    <p className="text-[0.75rem] text-gold/95 leading-snug">{L.cart.freeFrom3}</p>
                  </div>
                  <div className="mt-3" data-testid="cart-free-shipping-progress">
                    <div className="h-1 rounded-full bg-ivory/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-gold-deep to-gold transition-all duration-700" style={{ width: Math.min(100, (count / freeShipQty) * 100) + "%" }} />
                    </div>
                    <p className="mt-2 text-[0.7rem] tracking-wide text-ivory/70">
                      {freeShipping ? L.cart.unlocked : L.cart.progress(freeShipQty - count)}
                    </p>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4" data-testid="cart-items">
                  {items.map((i) => (
                    <div key={i.slug} className="flex gap-4" data-testid={"cart-item-" + i.slug}>
                      <img src={i.product.image} alt="" className="w-14 h-20 shrink-0 object-cover rounded-lg border border-gold/20" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display text-ivory truncate">{prodName(i.product, lang)}</p>
                          <button data-testid={"cart-item-remove-" + i.slug} onClick={() => removeItem(i.slug)} aria-label={L.cart.remove} title={L.cart.remove} className="shrink-0 flex items-center gap-1 p-1 text-ivory/50 hover:text-gold transition-colors">
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            <span className="text-[0.6rem] tracking-[0.15em] uppercase">{L.cart.remove}</span>
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className="text-gold text-sm">{fmtPrice(i.product.price, lang)} <span className="text-ivory/40 text-[0.65rem]">{L.cart.each}</span></p>
                          <p className="text-xs font-semibold text-ivory/70" data-testid={"cart-item-line-total-" + i.slug}>{fmtPrice(i.product.price * i.qty, lang)}</p>
                        </div>
                        <div className="flex items-center border border-gold/25 rounded-full mt-2 w-max">
                          <button data-testid={"cart-item-decrease-" + i.slug} onClick={() => setQty(i.slug, i.qty - 1)} className="p-1.5 text-ivory/70 hover:text-gold" aria-label="minus">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-7 text-center text-xs text-ivory" data-testid={"cart-item-qty-" + i.slug}>{i.qty}</span>
                          <button data-testid={"cart-item-increase-" + i.slug} onClick={() => setQty(i.slug, Math.min(99, i.qty + 1))} className="p-1.5 text-ivory/70 hover:text-gold" aria-label="plus">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gold/15 px-6 py-5 space-y-2">
                  <div className="flex justify-between text-sm text-ivory/80" data-testid="cart-subtotal">
                    <span>{L.cart.subtotal}</span>
                    <span>{fmtPrice(subtotal, lang)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-sm text-ivory/80" data-testid="cart-shipping">
                    <span>{L.cart.shipping} · {L.cart.country}</span>
                    <select
                      data-testid="checkout-destination-select"
                      value={dest}
                      onChange={(e) => setDest(e.target.value)}
                      className="bg-obsidian/70 border border-gold/25 rounded-full px-3 py-1.5 text-xs text-ivory focus:border-gold/70"
                    >
                      {destOptions.map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                      ))}
                    </select>
                    <span className={freeShipping ? "text-gold" : "text-ivory/80"}>{freeShipping ? L.cart.shipFree : fmtPrice(shipping, lang)}</span>
                  </div>
                  {!freeShipping && <p className="text-[0.62rem] text-ivory/45 leading-relaxed" data-testid="cart-rates-note">{L.cart.rates}</p>}
                  <div className="flex justify-between text-sm text-ivory/80" data-testid="cart-taxes">
                    <span>{L.cart.taxes}</span>
                    <span className="text-ivory/50">—</span>
                  </div>
                  <div className="flex justify-between font-display text-lg text-ivory pt-1" data-testid="cart-total">
                    <span>{L.cart.total}</span>
                    <span>{fmtPrice(total, lang)}</span>
                  </div>
                  <form onSubmit={submit} className="pt-3 space-y-2.5">
                    <input
                      data-testid="checkout-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder={L.cart.phonePlaceholder}
                      aria-label={L.cart.phone}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <p className="text-[0.6rem] tracking-[0.25em] uppercase text-gold/80 pt-1" data-testid="checkout-address-title">{L.cart.addrTitle}</p>
                    <input
                      data-testid="checkout-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={L.cart.namePlaceholder}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <input
                      data-testid="checkout-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={L.cart.emailPlaceholder}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <input
                      data-testid="checkout-address-input"
                      required
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder={L.cart.addressPlaceholder}
                      aria-label={L.cart.address}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <input
                      data-testid="checkout-apt-input"
                      value={form.apt}
                      onChange={(e) => setForm({ ...form, apt: e.target.value })}
                      placeholder={L.cart.apt}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <div className="flex gap-2.5">
                      <input
                        data-testid="checkout-city-input"
                        required
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder={L.cart.cityPlaceholder}
                        aria-label={L.cart.city}
                        className="flex-1 bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                      />
                      <input
                        data-testid="checkout-postal-input"
                        required
                        value={form.postal}
                        onChange={(e) => setForm({ ...form, postal: e.target.value })}
                        placeholder={L.cart.postalPlaceholder}
                        aria-label={L.cart.postal}
                        className="w-32 bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                      />
                    </div>
                    <input
                      data-testid="checkout-province-input"
                      required
                      value={form.province}
                      onChange={(e) => setForm({ ...form, province: e.target.value })}
                      placeholder={L.cart.provincePlaceholder}
                      aria-label={L.cart.province}
                      className="w-full bg-obsidian/60 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
                    />
                    <button
                      data-testid="checkout-submit-button"
                      type="submit"
                      disabled={sending}
                      className="btn-gold w-full rounded-full py-3.5 text-[0.7rem] tracking-[0.25em] uppercase font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <Lock className="w-3.5 h-3.5" /> {sending ? "…" : L.cart.checkout}
                    </button>
                    <button type="button" onClick={close} data-testid="cart-continue-shopping" className="w-full rounded-full py-3 text-[0.68rem] tracking-[0.25em] uppercase text-ivory/70 hover:text-gold border border-gold/25 hover:border-gold/60 transition-colors">
                      {L.cart.continue}
                    </button>
                    <p className="text-[0.6rem] text-center text-ivory/40">{L.pay.secure}</p>
                  </form>
                </div>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
